/**
 * @file paddle hub 详情
 * @author FengGuang(fengguang01@baidu.com)
 */
import React, {useCallback} from 'react';
import {Breadcrumb, Button} from 'antd';
import {
    ArrowUpOutlined,
    RightOutlined
} from '@ant-design/icons';

import Header from '../../components/header/HeaderWithStore';
import Footer from '../../components/footer/footer';
import Banner from '../../components/banner/Banner';
import ModelBaseStructureModal from '../../components/model-base-structure-modal/ModelBaseStructureModal';

import usePaddleHubDetailHooks from './modelBaseDetailHooks';
import MarkdownContainer from '../../components/markdown-container/MarkdownContainer';
import A from '../../components/a/A';

const ModelBaseDetail: React.FC = () => {
    const {
        modelDetail,
        backToModelBaseBreadcrumbUrl
    } = usePaddleHubDetailHooks();

    const onGetModelClick = useCallback(() => {
        if ((window as any)._hmt) {
            (window as any)._hmt.push([
                '_trackEvent',
                '模型库',
                '获取模型',
                `${modelDetail.getModelUrl}`
            ]);
        }
    }, [modelDetail.getModelUrl]);
    const onOnlineRunClick = useCallback(() => {
        if ((window as any)._hmt) {
            (window as any)._hmt.push([
                '_trackEvent',
                '模型库',
                '在线运行',
                `${modelDetail.onlineRunUrl}`
            ]);
        }
    }, [modelDetail.onlineRunUrl]);

    return (
        <div className="page mbd-page">
            <Header />
            <Banner
                title="模型库"
                subtitle={
                    <span>
                        覆盖图像、自然语言处理、推荐等各种方向的官方模型
                        <A
                            className="mbd-page-banner-link"
                            target
                            href="https://github.com/paddlepaddle/models"
                        >
                           GitHub <ArrowUpOutlined className="mbd-page-banner-icon" />
                        </A>
                        <A
                            className="mbd-page-banner-link"
                            target
                            href="https://gitee.com/paddlepaddle/models"
                        >
                           Gitee <ArrowUpOutlined className="mbd-page-banner-icon" />
                        </A>
                        <A
                            className="mbd-page-banner-link"
                            target
                            href="https://www.paddlepaddle.org.cn/start"
                        >
                           安装飞桨 <ArrowUpOutlined className="mbd-page-banner-icon" />
                        </A>
                    </span>
                }
                divider
            />

            <div className="mbd-page-container clear-both">
                <div className="mbd-page-container-right">
                    <Breadcrumb
                        className="mbd-page-container-breadcrumb"
                        separator={<RightOutlined className="mbd-page-container-breadcrumb-right" />}
                    >
                        <Breadcrumb.Item>
                            <a href='/modelbase'>
                                模型库
                            </a>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <a
                                href={backToModelBaseBreadcrumbUrl}
                            >
                                {modelDetail.type}
                            </a>
                        </Breadcrumb.Item>
                        {
                            !!modelDetail.name
                            && <Breadcrumb.Item>
                                {modelDetail.name}
                            </Breadcrumb.Item>
                        }

                    </Breadcrumb>

                    <div className="mbd-page-container-h1">
                        {modelDetail.name}
                    </div>

                    <div className="mbd-page-container-info">
                        <div className="mbd-page-container-info-item">
                            类别
                            <span className="mbd-page-container-info-item-data">
                                {modelDetail.type}
                            </span>
                        </div>
                        <div className="mbd-page-container-info-item">
                            应用
                            <span className="mbd-page-container-info-item-data">
                                {modelDetail.application}
                            </span>
                        </div>
                    </div>

                    <div className="mbd-page-container-link-btn-group">
                        {!!modelDetail.getModelUrl
                        && (
                            <a href={modelDetail.getModelUrl} onClick={onGetModelClick}>
                                <Button type="primary" size="large">
                                    获取模型
                                    <ArrowUpOutlined className="mbd-page-banner-icon" />
                                </Button>
                            </a>
                        )}
                        {!!modelDetail.onlineRunUrl
                        && (
                            <a href={modelDetail.onlineRunUrl} onClick={onOnlineRunClick}>
                                <Button type="primary" size="large">
                                    在线运行
                                    <ArrowUpOutlined className="mbd-page-banner-icon" />
                                </Button>
                            </a>
                        )}

                        <div className="mbd-page-container-link-btn-group-right">
                            {
                                !!modelDetail.paperUrl
                                && <a href={modelDetail.paperUrl}>
                                    查看论文
                                    <ArrowUpOutlined className="mbd-page-banner-icon" />
                                </a>
                            }
                            {
                                !!modelDetail.modelStructuralDiagramUrl
                                && <ModelBaseStructureModal
                                    imgSrc={modelDetail.modelStructuralDiagramUrl}
                                >
                                    <button
                                        className="link-button"
                                    >
                                        查看模型结构图
                                    </button>
                                </ModelBaseStructureModal>
                            }
                        </div>
                    </div>

                    <div className="mbd-page-container-h2">
                        模型概述
                    </div>
                    <div className="mbd-page-container-summary">
                        {modelDetail.summary}
                    </div>

                    <div className="mbd-page-container-h2">
                        模型说明
                    </div>
                    <div className="mbd-page-markdown-wrap">
                        <MarkdownContainer source={modelDetail.detail} />
                    </div>
                </div>
            </div>
            <Footer />

        </div>
    );
};

export default ModelBaseDetail;
